import { inject, injectable } from "inversify";
import { EngineConfigService, Logger } from "@designboticteam/core";
import {
	CursorSnappingService,
	type Polyline,
	PolylineTool,
	SceneService,
	type Shape,
	ShapeEditingService,
	Workspace3DStoreService,
} from "@designboticteam/workspace-3d";
import { Kolega3DStoreService } from "../../services/Kolega3DStoreService";
import { BuildingLine } from "./BuildingLine";

@injectable()
class BuildingLineTool extends PolylineTool {
	public static readonly toolName: string = "BuildingLineTool";
	public override readonly toolName: string = BuildingLineTool.toolName;
	protected override readonly logger = new Logger(`🧱 ${this.toolName}`, {
		color: "#ffffff",
		backgroundColor: "#16a34a",
	});

	private readonly buildingLines = new Set<BuildingLine>();

	constructor(
		@inject(SceneService) sceneService: SceneService,
		@inject(CursorSnappingService) cursorSnappingService: CursorSnappingService,
		@inject(ShapeEditingService) shapeEditingService: ShapeEditingService,
		@inject(Workspace3DStoreService) workspace3DStoreService: Workspace3DStoreService,
		@inject(EngineConfigService) private readonly engineConfigService: EngineConfigService,
		@inject(Kolega3DStoreService) private readonly kolega3DStoreService: Kolega3DStoreService,
	) {
		super(shapeEditingService, sceneService, cursorSnappingService, workspace3DStoreService);
	}

	public getBuildingLines(): ReadonlyArray<BuildingLine> {
		return [...this.buildingLines];
	}

	/** */
	public clearAll(): void {
		for (const buildingLine of this.buildingLines) {
			buildingLine.dispose();
			this.forgetShape(buildingLine);
		}

		this.buildingLines.clear();
		this.syncStore();
	}

	/** */
	public deleteShape(shape: Shape): boolean {
		if (shape instanceof BuildingLine === false || this.buildingLines.has(shape) === false) {
			return false;
		}

		shape.dispose();
		this.forgetShape(shape);
		this.buildingLines.delete(shape);
		this.syncStore();
		return true;
	}

	/** */
	protected override createShape(): Polyline {
		const palette = this.engineConfigService.palette;

		return new BuildingLine({
			lineColor: palette.main.tertiary,
			labelColor: palette.main.tertiary,
			vertexHandlesColor: palette.main.tertiary,
			vertexHandlesFillColor: palette.neutral.white,
			vertexHandlesHighlightColor: palette.tones.tertiary[40],
		});
	}

	/** */
	protected override onShapeAccepted(shape: Polyline): void {
		super.onShapeAccepted(shape);

		if (shape instanceof BuildingLine) {
			this.buildingLines.add(shape);
			this.syncStore();
		}
	}

	private syncStore(): void {
		this.kolega3DStoreService.store.setState({ buildingLines: [...this.buildingLines], isValidationDirty: true });
	}
}

export { BuildingLineTool };
